import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search, Filter, Download, Eye, CheckCircle, XCircle,
  MoreVertical, RefreshCw, Phone, MapPin, Star, Bike,
  Clock, UserCheck, UserX,
} from 'lucide-react';
import { Card } from '@/components/ui';
import { api } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

const mockDrivers = [
  { id: 1, name: 'خالد أحمد', phone: '01012345678', vehicle: 'دراجة نارية - Honda', plate: 'أ ب ج 1234', area: 'القاهرة', orders_count: 120, rating: 4.9, status: 'online', approved: true, earnings: 8500, created_at: '2025-06-10' },
  { id: 2, name: 'محمد سعيد', phone: '01098765432', vehicle: 'دراجة نارية - Suzuki', plate: 'د ه و 5678', area: 'الجيزة', orders_count: 95, rating: 4.7, status: 'online', approved: true, earnings: 6200, created_at: '2025-07-15' },
  { id: 3, name: 'عمر يوسف', phone: '01155544433', vehicle: 'سيارة - Toyota', plate: 'ز ح ط 9012', area: 'الإسكندرية', orders_count: 78, rating: 4.5, status: 'offline', approved: true, earnings: 5100, created_at: '2025-08-20' },
  { id: 4, name: 'أيمن محمود', phone: '01234567890', vehicle: 'دراجة نارية - Yamaha', plate: 'ي ك ل 3456', area: 'المنصورة', orders_count: 45, rating: 4.3, status: 'online', approved: true, earnings: 3200, created_at: '2025-09-01' },
  { id: 5, name: 'طارق حسن', phone: '01567890123', vehicle: 'دراجة نارية - Honda', plate: 'م ن س 7890', area: 'القاهرة', orders_count: 0, rating: 0, status: 'offline', approved: false, earnings: 0, created_at: '2026-03-20' },
  { id: 6, name: 'سامي فؤاد', phone: '01345678901', vehicle: 'سيارة - Hyundai', plate: 'ع ف ص 2345', area: 'الجيزة', orders_count: 0, rating: 0, status: 'offline', approved: false, earnings: 0, created_at: '2026-03-21' },
];

export default function DriversPage() {
  const { t, formatCurrency, language } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const { data: driversData, isLoading, refetch } = useQuery({
    queryKey: ['drivers'],
    queryFn: () => api.getDrivers({ page: 1 }),
  });

  const drivers = driversData?.data || mockDrivers;

  const filteredDrivers = drivers.filter((driver: any) => {
    const matchSearch = !searchQuery ||
      driver.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      driver.phone?.includes(searchQuery);
    const matchStatus = statusFilter === 'all' || 
      (statusFilter === 'online' && driver.status === 'online') ||
      (statusFilter === 'offline' && driver.status === 'offline' && driver.approved) ||
      (statusFilter === 'pending' && !driver.approved);
    return matchSearch && matchStatus;
  });

  const onlineCount = drivers.filter((d: any) => d.status === 'online').length;
  const pendingCount = drivers.filter((d: any) => !d.approved).length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">{t('drivers')}</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {language === 'ar' ? `إجمالي ${drivers.length} سائق` : `Total ${drivers.length} drivers`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => refetch()} className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
            <RefreshCw size={18} />
          </button>
          <button className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
            <Download size={18} />
            <span className="hidden sm:inline">{t('export')}</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Bike className="text-blue-600 dark:text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{language === 'ar' ? 'إجمالي' : 'Total'}</p>
              <p className="text-2xl font-bold text-slate-800 dark:text-white">{drivers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <UserCheck className="text-emerald-600 dark:text-emerald-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('online')}</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{onlineCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
              <UserX className="text-slate-500 dark:text-slate-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('offline')}</p>
              <p className="text-2xl font-bold text-slate-800 dark:text-white">{drivers.filter((d: any) => d.status === 'offline' && d.approved).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Clock className="text-amber-600 dark:text-amber-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{language === 'ar' ? 'في الانتظار' : 'Pending'}</p>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{pendingCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {[
          { key: 'all', label: t('all'), count: drivers.length },
          { key: 'online', label: t('online'), count: onlineCount },
          { key: 'offline', label: t('offline'), count: drivers.filter((d: any) => d.status === 'offline' && d.approved).length },
          { key: 'pending', label: language === 'ar' ? 'في الانتظار' : 'Pending', count: pendingCount },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              statusFilter === tab.key
                ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
          >
            {tab.label}
            <span className={`min-w-[24px] h-6 flex items-center justify-center rounded-full text-xs font-bold ${
              statusFilter === tab.key ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'
            }`}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <Card>
        <div className="p-4 flex flex-col sm:flex-row items-center gap-4">
          <div className="relative flex-1 w-full">
            <Search className="absolute top-1/2 -translate-y-1/2 start-4 text-slate-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('search')}
              className="w-full ps-12 pe-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
            />
          </div>
          <button className="flex items-center gap-2 px-5 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <Filter size={18} />
            {t('filter')}
          </button>
        </div>
      </Card>

      {/* Drivers Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-700">
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('driver')}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{language === 'ar' ? 'المركبة' : 'Vehicle'}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{language === 'ar' ? 'المنطقة' : 'Area'}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('orders')}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('rating')}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{language === 'ar' ? 'الأرباح' : 'Earnings'}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('status')}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="p-12 text-center">
                  <div className="inline-flex items-center gap-3 text-slate-500">
                    <RefreshCw className="animate-spin" size={20} />{t('loading')}
                  </div>
                </td></tr>
              ) : filteredDrivers.length === 0 ? (
                <tr><td colSpan={8} className="p-12 text-center">
                  <Bike size={48} className="mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                  <p className="text-slate-400 font-medium">{t('noData')}</p>
                </td></tr>
              ) : (
                filteredDrivers.map((driver: any) => (
                  <tr key={driver.id} className="border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                            {driver.name?.[0]}
                          </div>
                          {driver.status === 'online' && (
                            <div className="absolute -bottom-0.5 -end-0.5 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white dark:border-slate-800" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 dark:text-white">{driver.name}</p>
                          <p className="text-xs text-slate-400" dir="ltr">{driver.phone}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-slate-600 dark:text-slate-300">{driver.vehicle}</p>
                      <p className="text-xs text-slate-400" dir="ltr">{driver.plate}</p>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
                        <MapPin size={14} className="text-slate-400" />
                        {driver.area}
                      </div>
                    </td>
                    <td className="p-4 font-medium text-slate-800 dark:text-white">{driver.orders_count}</td>
                    <td className="p-4">
                      {driver.rating > 0 ? (
                        <div className="flex items-center gap-1">
                          <Star size={14} className="text-amber-400 fill-amber-400" />
                          <span className="font-medium text-slate-800 dark:text-white">{driver.rating}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">--</span>
                      )}
                    </td>
                    <td className="p-4 font-bold text-slate-800 dark:text-white">{formatCurrency(driver.earnings)}</td>
                    <td className="p-4">
                      {!driver.approved ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
                          <Clock size={14} />
                          {language === 'ar' ? 'في الانتظار' : 'Pending'}
                        </span>
                      ) : driver.status === 'online' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                          <CheckCircle size={14} />
                          {t('online')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                          <XCircle size={14} />
                          {t('offline')}
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {!driver.approved ? (
                          <>
                            <button className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors" title={t('approved')}>
                              <CheckCircle size={18} />
                            </button>
                            <button className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors" title={t('rejected')}>
                              <XCircle size={18} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 transition-colors">
                              <Eye size={18} />
                            </button>
                            <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 transition-colors">
                              <Phone size={18} />
                            </button>
                            <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 transition-colors">
                              <MoreVertical size={18} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
